import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Pencil, FilePlus2, Copy, Archive, Trash2, Loader2, ArrowLeft,
  ChevronDown, ChevronUp, Clock, Download, Users, ClipboardCheck,
  BookOpen, Eye, Star, ShoppingBag, FileText, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollapsiblePreview = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 dark:border-slate-800">
      <button
        className="w-full flex items-center justify-between py-4 text-left"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span className="font-semibold text-slate-800 dark:text-slate-200">{title}</span>
        {open ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
      </button>
      {open && <div className="pb-6">{children}</div>}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">{children}</h3>
);

const ProgrammeViewPage = () => {
  const { programmeId } = useParams();
  const navigate = useNavigate();
  const [programme, setProgramme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('programme');

  useEffect(() => {
    fetch(`${API}/programmes/${programmeId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setProgramme(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [programmeId]);

  const handleDuplicate = async () => {
    if (!programme) return;
    try {
      const { programme_id, internal_code, created_at, updated_at, ...rest } = programme;
      rest.title = `${rest.title} (copie)`;
      const res = await fetch(`${API}/programmes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rest)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Programme dupliqué');
        navigate(`/bibliotheque/programmes/${data.programme_id}`);
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archiver ce programme ?')) return;
    try {
      await fetch(`${API}/programmes/${programmeId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ published_online: false })
      });
      toast.success('Programme archivé');
      navigate('/bibliotheque/programmes');
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement ce programme ?')) return;
    try {
      await fetch(`${API}/programmes/${programmeId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Programme supprimé');
      navigate('/bibliotheque/programmes');
    } catch (e) { toast.error('Erreur'); }
  };

  const renderTextContent = (text) => {
    if (!text) return <p className="text-sm text-slate-400 italic">Non renseigné</p>;
    return text.split('\n').map((line, i) => (
      <p key={i} className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
        {line.trim() ? (line.startsWith('-') || line.startsWith('•')
          ? <span className="flex items-start gap-2"><span className="text-slate-400 mt-0.5">{'›'}</span>{line.replace(/^[-•]\s*/, '')}</span>
          : line
        ) : <br />}
      </p>
    ));
  };

  if (loading) return (
    <Layout><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div></Layout>
  );

  if (!programme) return (
    <Layout>
      <div className="text-center py-20">
        <p className="text-slate-500 mb-4">Programme non trouvé</p>
        <Button onClick={() => navigate('/bibliotheque/programmes')}>Retour</Button>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="programme-view-page">
        {/* Breadcrumb */}
        <div className="text-sm text-slate-500 flex items-center gap-1">
          <button onClick={() => navigate('/bibliotheque/programmes')} className="hover:text-teal-600 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Bibliothèque
          </button>
          <span className="mx-1">&gt;</span>
          <span className="text-teal-600 font-medium truncate max-w-md">{programme.title}</span>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{programme.title}</h1>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 dark:bg-slate-800 w-full justify-start overflow-x-auto">
            <TabsTrigger value="programme" data-testid="tab-mon-programme">Mon programme</TabsTrigger>
            <TabsTrigger value="evaluations">Évaluations</TabsTrigger>
            <TabsTrigger value="espace-apprenant">Espace Apprenant</TabsTrigger>
            <TabsTrigger value="documents">Documents privés</TabsTrigger>
            <TabsTrigger value="inscriptions">Inscriptions</TabsTrigger>
            <TabsTrigger value="qualite">Qualité</TabsTrigger>
            <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
          </TabsList>

          <TabsContent value="programme" className="space-y-6 mt-4">
            {/* Action buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                className="bg-teal-500 hover:bg-teal-600 text-white"
                onClick={() => navigate(`/bibliotheque/programmes/${programmeId}/edit`)}
                data-testid="edit-programme-btn"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Modifier le contenu
              </Button>
              <Button variant="outline" onClick={() => navigate(`/sessions/nouveau?programme=${programmeId}`)} data-testid="create-session-btn">
                <FilePlus2 className="h-4 w-4 mr-2" />
                Créer une session
              </Button>
              <Button variant="outline" onClick={handleDuplicate} data-testid="duplicate-btn">
                <Copy className="h-4 w-4 mr-2" />
                Dupliquer
              </Button>
              <Button variant="outline" onClick={handleArchive} data-testid="archive-btn">
                <Archive className="h-4 w-4 mr-2" />
                Archiver
              </Button>
              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600" onClick={handleDelete} data-testid="delete-programme-btn">
                <Trash2 className="h-5 w-5" />
              </Button>
            </div>

            {/* Generated info */}
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>Généré le {new Date(programme.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              {programme.version > 1 && <Badge variant="outline">v{programme.version}</Badge>}
              <span className="font-mono text-xs">{programme.internal_code || programme.programme_id}</span>
            </div>

            {/* Download row */}
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="text-sm">
                <Download className="h-4 w-4 mr-2" />
                Programme
                <Settings className="h-3 w-3 ml-2 text-slate-400" />
              </Button>
            </div>

            {/* Programme Preview */}
            <CollapsiblePreview title="Aperçu du programme" defaultOpen={true}>
              <Card className="bg-slate-50 dark:bg-slate-800/50 border-0">
                <CardContent className="p-8 space-y-8">
                  {/* Header */}
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{programme.title}</h2>
                      {programme.subtitle && (
                        <p className="text-teal-600 underline underline-offset-4 mt-2 text-lg">{programme.subtitle}</p>
                      )}
                      {programme.modalities && <p className="text-slate-600 mt-2">{programme.modalities}</p>}
                      {programme.competency_domains && (
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-sm text-slate-500">Compétences :</span>
                          <Badge variant="outline" className="text-sm">{programme.competency_domains}</Badge>
                        </div>
                      )}
                    </div>
                    {/* Duration + Photo */}
                    <div className="shrink-0 ml-8 text-right space-y-3">
                      {(programme.duration_hours > 0 || programme.duration_days > 0) && (
                        <div className="flex items-center gap-2 justify-end text-teal-600 font-medium">
                          <Clock className="h-5 w-5" />
                          <span>Durée : {programme.duration_hours > 0 ? `${programme.duration_hours} heures` : ''}{programme.duration_hours > 0 && programme.duration_days > 0 ? ' ' : ''}{programme.duration_days > 0 ? `(${programme.duration_days} jours)` : ''}</span>
                        </div>
                      )}
                      {programme.photo_url && (
                        <img src={programme.photo_url} alt="" className="w-48 h-32 rounded-xl object-cover" />
                      )}
                    </div>
                  </div>

                  {/* Objectifs pédagogiques */}
                  {programme.pedagogical_objectives && (
                    <div>
                      <SectionTitle>Objectifs pédagogiques</SectionTitle>
                      <div className="pl-4 border-l-2 border-teal-200 space-y-3">
                        {programme.pedagogical_objectives.split('\n').filter(l => l.trim()).map((obj, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <span className="text-teal-500 mt-0.5 shrink-0">&#10003;</span>
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{obj.replace(/^[-•a-z]\)\s*/i, '')}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Profil des apprenants */}
                  {programme.learner_profile && (
                    <div>
                      <SectionTitle>Profil des apprenants</SectionTitle>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                            <Users className="h-4 w-4" /> Pour qui :
                          </p>
                          {renderTextContent(programme.learner_profile)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contenu de la formation */}
                  {programme.training_content && (
                    <div>
                      <SectionTitle>Contenu de la formation</SectionTitle>
                      <div className="space-y-2">
                        {programme.training_content.split('\n').filter(l => l.trim()).map((block, i) => (
                          <div key={i} className="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-4">
                            <p className="text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                              <span className="text-teal-500 font-bold">+</span>
                              {block}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {programme.description && (
                    <div>
                      <SectionTitle>Description du programme</SectionTitle>
                      {renderTextContent(programme.description)}
                    </div>
                  )}

                  {/* 3 columns: Equipe / Suivi / Ressources */}
                  {(programme.execution_monitoring || programme.quality || programme.certification_modalities) && (
                    <div className="grid grid-cols-3 gap-6 pt-4 border-t">
                      <div className="text-center">
                        <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                          <Users className="h-6 w-6 text-slate-500" />
                        </div>
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">Équipe pédagogique</h4>
                        <div className="text-sm text-slate-600 text-left">
                          {renderTextContent(programme.quality || 'Non renseigné')}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                          <ClipboardCheck className="h-6 w-6 text-slate-500" />
                        </div>
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">Suivi de l'exécution et évaluation</h4>
                        <div className="text-sm text-slate-600 text-left">
                          {renderTextContent(programme.execution_monitoring)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                          <BookOpen className="h-6 w-6 text-slate-500" />
                        </div>
                        <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">Ressources techniques et pédagogiques</h4>
                        <div className="text-sm text-slate-600 text-left">
                          {renderTextContent(programme.certification_modalities || 'Non renseigné')}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </CollapsiblePreview>

            {/* Additional collapsible sections */}
            <CollapsiblePreview title="Bloc de programmes" defaultOpen={false}>
              <div className="text-sm text-slate-500">
                {programme.competency_block || <span className="italic">Aucun bloc de programmes défini</span>}
              </div>
            </CollapsiblePreview>

            <CollapsiblePreview title="Fichiers de programme externe" defaultOpen={false}>
              <div className="text-sm text-slate-500 italic">Aucun fichier externe attaché</div>
            </CollapsiblePreview>

            {/* Pricing summary */}
            {(programme.pricing_enterprise > 0 || programme.pricing_individual > 0) && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Tarification</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                      <p className="text-xs text-slate-500 mb-1">Entreprise</p>
                      <p className="text-xl font-bold text-slate-900">{programme.pricing_enterprise} €</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                      <p className="text-xs text-slate-500 mb-1">Particulier</p>
                      <p className="text-xl font-bold text-slate-900">{programme.pricing_individual} €</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                      <p className="text-xs text-slate-500 mb-1">Indépendant</p>
                      <p className="text-xl font-bold text-slate-900">{programme.pricing_independent} €</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Other tabs - placeholder content */}
          <TabsContent value="evaluations" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              <Star className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>Évaluations liées à ce programme</p>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="espace-apprenant" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>Espace apprenant du programme</p>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>Documents privés du programme</p>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="inscriptions" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>Inscriptions au programme</p>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="qualite" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>Indicateurs qualité du programme</p>
            </CardContent></Card>
          </TabsContent>
          <TabsContent value="marketplace" className="mt-4">
            <Card><CardContent className="py-12 text-center text-slate-500">
              <ShoppingBag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>Publication sur la marketplace</p>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ProgrammeViewPage;
